import { ClickAwayListener, SlotComponentProps } from "@mui/base";
import {
  debounce,
  InputBase,
  InputBaseProps,
  MenuItem,
  Paper,
  Popper,
  setRef,
  TextField,
  TextFieldProps,
  useForkRef,
} from "@mui/material";
import { TimeField } from "@mui/x-date-pickers";
import { TimeFieldOwnerState } from "@mui/x-date-pickers/TimeField/TimeField.types";
import { DateTime } from "luxon";
import { omit } from "ramda";
import React, { FocusEventHandler, forwardRef, PointerEventHandler, useCallback, useRef, useState } from "react";
import { useDayTimeInput } from "./hooks";
import { AutocompleteListbox, AutocompletePaper, AutocompletePopper } from "./TimeInputSlots";
import { TimeInputBaseProps } from "./types";
import { DayTimePickerOption } from "@sinch/components/form/DayTimeInput/DayTimePickerOption";

/**
 * Base time input without label, helper text etc...
 */
export const DayTimeInputBase = forwardRef<HTMLDivElement, TimeInputBaseProps>(
  (
    {
      error,
      minutesStep = 15,
      onChange,
      timePoint: timePointString,
      value: stringValue,
      disabled,
      optionsRangeInMinutes,
      inputRef: inputRefProp,
      disablePortal = true,
      referenceDate,
      includeTime,
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLDivElement>(null);
    const refSetter = useForkRef(ref, inputRef);

    const debouncedOnChange = debounce((currentVal) => {
      onChange(currentVal);
    }, 200);

    const value = stringValue
      ? DateTime.fromFormat(stringValue, "HH:mm", { zone: "UTC" }).set({
          year: 1970,
          month: 1,
          day: 1,
        })
      : null;
    const timePoint = timePointString
      ? DateTime.fromFormat(timePointString, "HH:mm", { zone: "UTC" }).set({
          year: 1970,
          month: 1,
          day: 1,
        })
      : null;

    const { options, onChangeHandler, onBlur } = useDayTimeInput({
      value: value ?? DateTime.fromMillis(0, { zone: "UTC" }),
      minutesStep,
      onChange: debouncedOnChange,
      optionsRangeInMinutes: optionsRangeInMinutes ?? 60 * 24 * 7,
      referenceDate: timePoint ?? referenceDate,
      limitInclude: includeTime,
    });

    const [dropdownAnchorEl, setDropdownAnchorEl] = useState<HTMLElement | null>(null);
    const [focused, setFocused] = useState<boolean>(false);
    const popupOpen = Boolean(dropdownAnchorEl);

    const handleFocus: FocusEventHandler<HTMLInputElement> = () => {
      setFocused(true);
      setDropdownAnchorEl(inputRef.current ?? null);
    };
    const handlePointerDown: PointerEventHandler<HTMLInputElement> = () => {
      if (focused) {
        setDropdownAnchorEl(inputRef.current ?? null);
      }
    };

    const handleChangeSelected = useCallback(
      (val: DateTime | null) => {
        setDropdownAnchorEl(null);
        onChangeHandler(val);
      },
      [onChangeHandler]
    );

    return (
      <>
        <TimeField<DateTime>
          ref={refSetter}
          {...omit(["as", "className", "anchorEl"], props)}
          disabled={disabled}
          inputProps={{
            ...props.inputProps,
            "data-cy": "day-time-input",
            onFocus: handleFocus,
            onBlur: () => {
              setFocused(false);
            },
            onPointerDown: handlePointerDown,
          }}
          inputRef={(inputEl) => {
            setRef<any>(inputRefProp, inputEl);
          }}
          maxTime={DateTime.fromMillis(0)}
          minTime={DateTime.fromMillis(1000 * 60 * 60 * 24)}
          onBlur={(e) => {
            onBlur();
            props.onBlur?.(e);
          }}
          onChange={onChangeHandler}
          readOnly={props.readOnly}
          referenceDate={referenceDate}
          slotProps={{
            textField: {
              error: error,
              onFocus: props.onFocus,
              sx: {
                ...props.sx,
                height: "100%",
                input: {
                  height: "100%",
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              },
            } as SlotComponentProps<typeof TextField, unknown, TimeFieldOwnerState<DateTime, false>>,
          }}
          slots={{ textField: TimePickerInputBase }}
          timezone="utc"
          value={value}
        />
        <AutocompletePopper
          anchorEl={dropdownAnchorEl}
          as={Popper}
          className="MuiAutocomplete-popper"
          data-cy="day-time-input-popper"
          disablePortal={disablePortal}
          open={popupOpen && !props.readOnly}
          placement="bottom-start"
          role="presentation"
          //sx={{ width: "100%" }}
        >
          <ClickAwayListener
            onClickAway={() => {
              if (!focused) {
                setDropdownAnchorEl(null);
              }
            }}
          >
            <AutocompletePaper
              as={Paper}
              className="MuiPaper-root MuiPaper-elevation MuiPaper-rounded MuiPaper-elevation1 MuiAutocomplete-paper"
            >
              <AutocompleteListbox style={{ padding: 0, margin: 0, maxHeight: "300px" }}>
                {options.map((option) => (
                  <MenuItem
                    key={option.toFormat("HH:mm")}
                    ref={(el) => {
                      if (option.setZone("utc").toFormat("HH:mm") === stringValue) {
                        el?.scrollIntoView({ block: "center" });
                      }
                    }}
                    className="SinchTimePickerOption-root"
                    data-cy="timeInputOption-date"
                    onClick={() => {
                      handleChangeSelected(option);
                    }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                    }}
                    selected={option.setZone("utc").toFormat("HH:mm") === stringValue}
                    style={{ height: `33px` }}
                  >
                    <DayTimePickerOption option={option} timePoint={timePoint} />
                  </MenuItem>
                ))}
              </AutocompleteListbox>
            </AutocompletePaper>
          </ClickAwayListener>
        </AutocompletePopper>
      </>
    );
  }
);
DayTimeInputBase.displayName = "TimeInputBase";

const TimePickerInputBase = forwardRef<HTMLInputElement, InputBaseProps & { InputProps: TextFieldProps["InputProps"] }>(
  ({ InputProps, ...props }, ref) => <InputBase {...props} {...InputProps} ref={ref} />
);
TimePickerInputBase.displayName = "TimePickerInputBase";
